import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ReactComponent as Moped } from '../assets/moped.svg';
import { useReducedMotion } from '../lib';
import { hopHeight, hopKeyframes, hopTiming, roadCss } from '../road/road.styles';
import { ApplyText } from '../typography';
import { stopCentre, useStyles } from './IntakeJourneyTracker.styles';

/**
 * How long the screen settles before the scooter sets off. Long enough to be read as the
 * application moving on rather than as the page still loading.
 */
const ADVANCE_DELAY_MS = 900;

/** The hop clears whatever is above it by this much, and never reads as less than this tall. */
const SKY_GAP_PX = 8;
const MIN_SKY_PX = 14;

/**
 * The clear sky above the scooter, measured when the hop is asked for rather than on mount —
 * it costs one read and it cannot be wrong about a layout that has since reflowed.
 *
 * The ceiling is whatever the composing screen put directly above the tracker: on the
 * post-interview screen that is the card's body paragraph, which is exactly what a five-star
 * jump would otherwise land in. With nothing above it, the tracker's own top serves — the
 * scooter is hung above that top, so the gap is real either way.
 */
const readHeadroom = (root: HTMLElement | null, rider: HTMLElement | null) => {
  if (!root || !rider) return MIN_SKY_PX;

  const ceiling = root.previousElementSibling;
  const ceilingBottom = ceiling ? ceiling.getBoundingClientRect().bottom : root.getBoundingClientRect().top;

  return Math.max(MIN_SKY_PX, Math.round(rider.getBoundingClientRect().top - ceilingBottom - SKY_GAP_PX));
};

export type IntakeJourneyTrackerHandle = {
  /**
   * Plays the scooter's hop for a rating of 1–5, as high as the score, with the backflip at
   * five. Imperative because it answers a tap: the same rating tapped twice has to hop twice,
   * and a prop carrying the same value again would not.
   */
  celebrate: (rating: number) => void;
};

export type IntakeJourneyTrackerProps = {
  /** Stop labels, in order. Two or more. */
  stops: string[];
  /** How many stops are already behind the candidate. */
  doneCount: number;
  /**
   * Where the scooter rides to, shortly after mount. Omit for a tracker that does not move —
   * the scooter then simply sits on the last completed stop.
   */
  activeIndex?: number;
  /**
   * The tracker's accessible name. Callers pass the arrived-at wording; it is applied when
   * the scooter lands, so the motion carries meaning rather than only decoration.
   */
  ariaLabel: string;
  /** The name before the scooter sets off. Defaults to `ariaLabel`. */
  initialAriaLabel?: string;
};

/**
 * The candidate's journey as four stops on a road, with the scooter riding to the one that
 * is happening now.
 *
 * The trail and the scooter are the progress bar's own — `roadCss.fill`, `roadCss.rider`,
 * `roadCss.vehicle` — so the dashes, the mask, the flowing trim and the engine jitter are
 * identical to the bar the candidate saw on every earlier screen. Only the geometry differs:
 * this one is pinned to stop centres rather than to a percentage.
 *
 * Reduced motion needs no branch for the ride: `ApplyStage`'s viewport rule collapses every
 * animation and transition inside the flow, so the scooter is simply already there. The hop is
 * the exception — a script animation is out of the stylesheet's reach, so it reads the
 * preference itself and does nothing.
 */
export const IntakeJourneyTracker = forwardRef<IntakeJourneyTrackerHandle, IntakeJourneyTrackerProps>(
  ({ stops, doneCount, activeIndex, ariaLabel, initialAriaLabel }, ref) => {
    const total = stops.length;
    const classes = useStyles({ total });
    const isReducedMotion = useReducedMotion();

    const rootRef = useRef<HTMLDivElement>(null);
    const riderRef = useRef<HTMLSpanElement>(null);
    /**
     * The hop lands on the vehicle, never on the rider above it: the rider carries the
     * `translateX(-50%)` that centres the scooter on its stop, and a transform animation there
     * would drop it — so the scooter would slide half its width sideways as it jumped. The
     * vehicle is also where the idle jitter lives, which is what lets this take over and hand
     * back. (`ApplyProgress` animates its rider instead, because nothing centres that one.)
     */
    const vehicleRef = useRef<HTMLSpanElement>(null);

    const [hasAdvanced, setHasAdvanced] = useState(false);

    const hasAdvance = activeIndex !== undefined && activeIndex > doneCount - 1;

    useEffect(() => {
      if (!hasAdvance) return;

      const timeout = setTimeout(() => setHasAdvanced(true), ADVANCE_DELAY_MS);

      return () => clearTimeout(timeout);
    }, [hasAdvance]);

    useImperativeHandle(
      ref,
      () => ({
        celebrate: (rating: number) => {
          const vehicle = vehicleRef.current;

          if (!vehicle || !vehicle.animate || isReducedMotion || rating <= 0) return;

          const headroom = readHeadroom(rootRef.current, riderRef.current);

          vehicle.animate(hopKeyframes(hopHeight(headroom, rating), rating), hopTiming(rating));
        },
      }),
      [isReducedMotion],
    );

    // Where the scooter is now: on the last completed stop until it sets off, then on the
    // active one. Both are stop indices, so the trail and the scooter read the same source.
    const restingIndex = Math.max(doneCount - 1, 0);
    const currentIndex = hasAdvanced && activeIndex !== undefined ? activeIndex : restingIndex;

    const centre = stopCentre(currentIndex, total);
    const firstCentre = stopCentre(0, total);

    // The name describes where the scooter actually is, so a screen reader that re-reads the
    // group after the ride reports the new stop rather than the one it set off from.
    const isBeforeAdvance = hasAdvance && !hasAdvanced;
    const label = isBeforeAdvance ? initialAriaLabel ?? ariaLabel : ariaLabel;

    return (
      <div ref={rootRef} css={classes.root} role="group" aria-label={label}>
        <div css={classes.rail} />
        <div css={[roadCss.fill, classes.trail]} style={{ width: `${centre - firstCentre}%` }} />

        <span ref={riderRef} css={[roadCss.rider, classes.rider]} style={{ left: `${centre}%` }} aria-hidden="true">
          <span ref={vehicleRef} css={[roadCss.vehicle, classes.vehicle]}>
            <Moped />
          </span>
        </span>

        <div css={classes.cols}>
          {stops.map((stop, index) => {
            // A stop is lit once the scooter has reached it — which is what makes the ride
            // mean something rather than just move something.
            const isDone = index < doneCount || index <= currentIndex;

            return (
              <div key={stop} css={classes.col}>
                <i css={[classes.dot, isDone && classes.dotDone]} />
                <div css={classes.label}>
                  <ApplyText role="label" tone={isDone ? 'ink' : 'muted'}>
                    {stop}
                  </ApplyText>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

IntakeJourneyTracker.displayName = 'IntakeJourneyTracker';
